import { type KeyboardEvent } from 'react';

import clsx from 'clsx';
import { LuBlocks, LuMilestone, LuOrbit } from 'react-icons/lu';

import { Button } from '../../Components/Button';

import { CardSparkle } from './CardSparkle';

import styles from './styles.module.scss';

interface PodStrings {
  heading: string;
  subheading: string;
  cta: string;
}

interface PodPaths {
  href?: string;
  onClick?: () => void;
}

interface CreateKidsWayPodProps {
  className?: string;
  strings?: PodStrings;
  icon: React.ReactNode;
  imageUrl: string;
  href?: string;
  onClick?: () => void;
}

export const CreateKidsWayPod = ({ className, strings, icon, href, onClick, imageUrl }: CreateKidsWayPodProps) => {
  const handleKeyDown = (e: KeyboardEvent<HTMLAnchorElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && onClick) {
      e.preventDefault();
      onClick();
    }
  };

  return (
    <a
      className={clsx(styles.waypod, className)}
      href={href}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={href ? undefined : 'button'}
      tabIndex={0}
    >
      <CardSparkle />

      <div className={styles['waypod-inner']}>
        <img src={imageUrl} alt={strings?.heading} />

        <div className={styles['waypod-label']}>
          {icon}
          <h3>{strings?.heading}</h3>
          <p>{strings?.subheading}</p>
        </div>
      </div>
    </a>
  );
};

interface CreateKidsWaypointProps {
  className?: string;
  canUseTeacherCreator?: boolean;
  showCyoa?: boolean;
  strings: {
    title: string;
    subtitle: string;
    swticherCta: string;
    stepByStep: PodStrings;
    oneShot: PodStrings;
    cyoa: PodStrings;
  };
  paths: {
    stepByStep: PodPaths;
    oneShot: PodPaths;
    cyoa: PodPaths;
    switcher: PodPaths;
  };
  images: {
    stepByStep: string;
    oneShot: string;
    cyoa: string;
  };
}

export const CreateKidsWaypoint = ({
  className,
  canUseTeacherCreator,
  showCyoa = false,
  strings,
  paths,
  images,
  footer,
}: CreateKidsWaypointProps & { footer?: React.ReactNode }) => (
  <div className={clsx(styles['waypoint-container'], className)}>
    <h2 className={styles.title}>{strings.title}</h2>

    <p className={styles.subtitle}>{strings.subtitle}</p>

    <div className={styles['waypods-container']}>
      <CreateKidsWayPod
        strings={strings.stepByStep}
        icon={<LuBlocks />}
        href={paths.stepByStep.href}
        onClick={paths.stepByStep.onClick}
        imageUrl={images.stepByStep}
      />

      <CreateKidsWayPod
        strings={strings.oneShot}
        icon={<LuOrbit />}
        href={paths.oneShot.href}
        onClick={paths.oneShot.onClick}
        imageUrl={images.oneShot}
      />

      {showCyoa && (
        <CreateKidsWayPod
          strings={strings.cyoa}
          icon={<LuMilestone />}
          href={paths.cyoa.href}
          onClick={paths.cyoa.onClick}
          imageUrl={images.cyoa}
        />
      )}
    </div>

    {(canUseTeacherCreator || footer) && (
      <div className={styles['footer-buttons']}>
        {footer}
        {canUseTeacherCreator && (
          <Button href={paths.switcher.href} onClick={paths.switcher.onClick} variant="text">
            {strings.swticherCta}
          </Button>
        )}
      </div>
    )}
  </div>
);
